import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Grid,
  Slider
} from '@mui/material';
import { styled } from '@mui/system';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CommentIcon from '@mui/icons-material/Comment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WatermarkIcon from '@mui/icons-material/BrandingWatermark';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../supabaseClient';
import { applyWatermark } from '../utils/applyWatermark';

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.9rem',
  minHeight: 48,
  padding: '6px 16px',
  borderRadius: '8px 8px 0 0',
  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.2)' : 'rgba(138, 43, 226, 0.1)',
  }
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
  margin: theme.spacing(0.5),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
  }
}));

const ShareDialog = ({ 
  open, 
  onClose, 
  itemType, // 'image', 'property', etc.
  itemId,
  itemName,
  thumbnailUrl,
  userId,
  includeAllImages = false
}) => {
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [propertyImages, setPropertyImages] = useState([]);
  const [shareSettings, setShareSettings] = useState({
    permission: 'view', // view, comment, edit
    expiration: '7days', // never, 24hours, 7days, 30days, custom
    customExpiration: null,
    password: '',
    passwordEnabled: false,
    watermark: true,
    allowDownload: false,
    isPublic: false
  });
  const [existingShares, setExistingShares] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userLogos, setUserLogos] = useState([]);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState('');
  const [useCustomLogo, setUseCustomLogo] = useState(false);
  const [watermarkPosition, setWatermarkPosition] = useState('bottom-right');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.7);
  const [watermarkScale, setWatermarkScale] = useState(0.15);

  // Fetch existing shares, property images, and user logos when dialog opens
  useEffect(() => {
    if (open && itemId) {
      fetchExistingShares();
      
      if (itemType === 'property') {
        fetchPropertyImages(itemId);
      }
      
      if (userId) {
        fetchUserLogos();
      }
    }
  }, [open, itemId, itemType, userId]);

  const fetchPropertyImages = useCallback(async (propertyId) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('property_id', propertyId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setPropertyImages(data || []);
    } catch (err) {
      console.error('Error fetching property images:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchUserLogos = async () => {
    try {
      const { data, error } = await supabase
        .from('user_logos')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      setUserLogos(data || []);
      
      // If user has logos but none selected, select the first one by default
      if (data && data.length > 0 && !selectedLogoUrl) {
        setSelectedLogoUrl(data[0].logo_url);
      }
    } catch (err) {
      console.error('Error fetching user logos:', err);
    }
  };

  const fetchExistingShares = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('shared_links')
        .select('*')
        .eq('item_id', itemId)
        .eq('item_type', itemType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setExistingShares(data || []);
    } catch (err) {
      console.error('Error fetching existing shares:', err);
      setError('Paylaşım bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSettingChange = (setting, value) => {
    setShareSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const getExpirationDate = () => {
    const now = new Date();
    
    switch (shareSettings.expiration) {
      case 'never':
        return null;
      case '24hours':
        return new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
      case '7days':
        return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30days':
        return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
      case 'custom':
        return shareSettings.customExpiration ? new Date(shareSettings.customExpiration).toISOString() : null;
      default:
        return null;
    }
  };

  const createShareLink = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Validate required fields
      if (shareSettings.passwordEnabled && !shareSettings.password) {
        setError('Şifre koruması etkinleştirildiğinde şifre alanı boş olamaz.');
        setLoading(false);
        return;
      }
      
      let finalImageUrl = thumbnailUrl; // Start with the original thumbnail URL
      let watermarkedPropertyImages = [];
      
      // Apply custom logo watermark if enabled
      if (useCustomLogo && selectedLogoUrl) {
        try {
          console.log("Applying custom logo watermark...");
          
          // For property with multiple images, watermark all of them
          if (itemType === 'property' && propertyImages.length > 0) {
            // Process all property images with watermark
            for (const image of propertyImages) {
              // Apply watermark to each image
              const watermarkedBlob = await applyWatermark(image.url, selectedLogoUrl, {
                position: watermarkPosition,
                opacity: watermarkOpacity,
                scale: watermarkScale
              });
              
              // Upload the watermarked image
              const fileName = `watermarked-${itemType}-${image.id}-${Date.now()}.png`;
              const filePath = `${userId}/${fileName}`;
              
              const { data: uploadData, error: uploadError } = await supabase.storage
                .from('user-logos') // Use the same bucket as logos
                .upload(filePath, watermarkedBlob, {
                  contentType: 'image/png',
                  upsert: false
                });
                
              if (uploadError) throw uploadError;
              
              // Get the public URL of the watermarked image
              const { data: urlData } = supabase.storage
                .from('user-logos')
                .getPublicUrl(filePath);
                
              if (!urlData?.publicUrl) {
                throw new Error("Failed to get public URL for watermarked image");
              }
              
              // Store the original image ID and its watermarked URL
              watermarkedPropertyImages.push({
                id: image.id,
                originalUrl: image.url,
                watermarkedUrl: urlData.publicUrl
              });
              
              // Use the first image as the thumbnail
              if (watermarkedPropertyImages.length === 1) {
                finalImageUrl = urlData.publicUrl;
              }
            }
            
            console.log(`Watermarked ${watermarkedPropertyImages.length} property images`);
          } else {
            // For single image, just watermark the thumbnail
            const watermarkedBlob = await applyWatermark(thumbnailUrl, selectedLogoUrl, {
              position: watermarkPosition,
              opacity: watermarkOpacity,
              scale: watermarkScale
            });
            
            // Upload the watermarked image
            const fileName = `watermarked-${itemType}-${itemId}-${Date.now()}.png`;
            const filePath = `${userId}/${fileName}`;
            
            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('user-logos') // Use the same bucket as logos
              .upload(filePath, watermarkedBlob, {
                contentType: 'image/png',
                upsert: false
              });
              
            if (uploadError) throw uploadError;
            
            // Get the public URL of the watermarked image
            const { data: urlData } = supabase.storage
              .from('user-logos')
              .getPublicUrl(filePath);
              
            if (!urlData?.publicUrl) {
              throw new Error("Failed to get public URL for watermarked image");
            }
            
            finalImageUrl = urlData.publicUrl;
            console.log("Watermarked image URL:", finalImageUrl);
          }
          
        } catch (watermarkError) {
          console.error("Error applying watermark:", watermarkError);
          setError(`Filigran uygulanırken bir hata oluştu: ${watermarkError.message}`);
          setLoading(false);
          return;
        }
      }
      
      // Generate a unique token for the share
      const shareToken = uuidv4();
      
      // Create the share record
      const shareData = {
        id: shareToken,
        user_id: userId,
        item_id: itemId,
        item_type: itemType,
        item_name: itemName,
        thumbnail_url: finalImageUrl, // Use watermarked image if applicable
        permission: shareSettings.permission,
        expires_at: getExpirationDate(),
        password: shareSettings.passwordEnabled ? shareSettings.password : null,
        has_watermark: useCustomLogo ? true : shareSettings.watermark,
        custom_watermark: useCustomLogo,
        watermark_logo_url: useCustomLogo ? selectedLogoUrl : null,
        allow_download: shareSettings.allowDownload,
        is_public: shareSettings.isPublic,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        view_count: 0,
        // Store watermarked images as a JSON string to ensure compatibility
        watermarked_images: watermarkedPropertyImages.length > 0 ? JSON.stringify(watermarkedPropertyImages) : null
      };
      
      // For properties with multiple images, we'll just use the first as thumbnail
      // and create separate share links for each image
      if (itemType === 'property' && propertyImages.length > 0 && !useCustomLogo) {
        // Use the first image as the thumbnail
        shareData.thumbnail_url = propertyImages[0].url;
      }
      
      const { error } = await supabase
        .from('shared_links')
        .insert(shareData);
        
      if (error) {
        console.error('Error creating share:', error);
        throw new Error(`Paylaşım oluşturulurken bir hata oluştu: ${error.message}`);
      }
      
      // Generate the shareable URL
      const shareUrl = `${window.location.origin}/share/${shareToken}`;
      setShareLink(shareUrl);
      setSuccess('Paylaşım bağlantısı başarıyla oluşturuldu!');
      
      // Refresh the list of existing shares
      fetchExistingShares();
    } catch (err) {
      console.error('Error creating share link:', err);
      setError('Paylaşım bağlantısı oluşturulurken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const deleteShareLink = async (shareId) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('shared_links')
        .delete()
        .eq('id', shareId);
        
      if (error) throw error;
      
      // Update the list of existing shares
      setExistingShares(prev => prev.filter(share => share.id !== shareId));
      setSuccess('Paylaşım bağlantısı başarıyla silindi.');
    } catch (err) {
      console.error('Error deleting share link:', err);
      setError('Paylaşım bağlantısı silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'view':
        return <VisibilityIcon fontSize="small" />;
      case 'comment':
        return <CommentIcon fontSize="small" />;
      case 'edit':
        return <EditIcon fontSize="small" />;
      default:
        return <VisibilityIcon fontSize="small" />;
    }
  };

  const getExpirationText = (expiresAt) => {
    if (!expiresAt) return 'Süresiz';
    
    const expDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Süresi doldu';
    if (diffDays === 1) return 'Bugün sona eriyor';
    if (diffDays < 7) return `${diffDays} gün kaldı`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta kaldı`;
    return `${Math.floor(diffDays / 30)} ay kaldı`;
  };

  const shareToSocial = (platform) => {
    if (!shareLink) {
      setError('Önce bir paylaşım bağlantısı oluşturun.');
      return;
    }
    
    let url = '';
    const text = `${itemName} - Sanalyapı üzerinden paylaşıldı`;
    
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareLink)}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent('Merhaba,\n\nBu bağlantıdan tasarımımı inceleyebilirsin: ' + shareLink)}`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        pb: 1
      }}>
        <Typography variant="h6">Paylaşım Seçenekleri</Typography>
        <Chip 
          label={itemType === 'property' ? 'Mülk' : 'Görsel'} 
          size="small"
          color="primary"
          variant="outlined"
        />
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ 
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            mb: 2
          }}
        >
          <StyledTab label="Yeni Paylaşım" />
          <StyledTab 
            label={`Mevcut Paylaşımlar (${existingShares.length})`} 
            disabled={loading}
          />
        </Tabs>
        
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              mb: 3
            }}>
              {/* Left column - Share settings */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Paylaşım Ayarları
                </Typography>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="permission-label">İzin Seviyesi</InputLabel>
                  <Select
                    labelId="permission-label"
                    value={shareSettings.permission}
                    label="İzin Seviyesi"
                    onChange={(e) => handleSettingChange('permission', e.target.value)}
                  >
                    <MenuItem value="view">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
                        <Typography>Sadece görüntüleme</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="comment">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CommentIcon sx={{ mr: 1 }} fontSize="small" />
                        <Typography>Görüntüleme ve yorum</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="edit">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EditIcon sx={{ mr: 1 }} fontSize="small" />
                        <Typography>Tam erişim</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="expiration-label">Geçerlilik Süresi</InputLabel>
                  <Select
                    labelId="expiration-label"
                    value={shareSettings.expiration}
                    label="Geçerlilik Süresi"
                    onChange={(e) => handleSettingChange('expiration', e.target.value)}
                  >
                    <MenuItem value="never">Süresiz</MenuItem>
                    <MenuItem value="24hours">24 Saat</MenuItem>
                    <MenuItem value="7days">7 Gün</MenuItem>
                    <MenuItem value="30days">30 Gün</MenuItem>
                    <MenuItem value="custom">Özel Tarih</MenuItem>
                  </Select>
                </FormControl>
                
                {shareSettings.expiration === 'custom' && (
                  <TextField
                    label="Bitiş Tarihi"
                    type="date"
                    fullWidth
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                    value={shareSettings.customExpiration || ''}
                    onChange={(e) => handleSettingChange('customExpiration', e.target.value)}
                  />
                )}
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={shareSettings.passwordEnabled}
                      onChange={(e) => handleSettingChange('passwordEnabled', e.target.checked)}
                    />
                  }
                  label="Şifre Koruması"
                  sx={{ mb: 1 }}
                />
                
                {shareSettings.passwordEnabled && (
                  <TextField
                    label="Şifre"
                    type="password"
                    fullWidth
                    sx={{ mb: 2 }}
                    value={shareSettings.password}
                    onChange={(e) => handleSettingChange('password', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>
              
              {/* Right column - Additional options */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Ek Seçenekler
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Filigran Seçenekleri
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={useCustomLogo}
                        onChange={(e) => {
                          setUseCustomLogo(e.target.checked);
                          if (!e.target.checked) {
                            // If turning off custom logo, enable default watermark
                            handleSettingChange('watermark', true);
                          } else {
                            // If turning on custom logo, disable default watermark
                            handleSettingChange('watermark', false);
                          }
                        }}
                      />
                    }
                    label="Özel Logo Filigranı Ekle"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  
                  {useCustomLogo ? (
                    <Box sx={{ ml: 3, mb: 2 }}>
                      {userLogos.length === 0 ? (
                        <Box>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Henüz logo yüklemediniz. Profil ayarlarından logo ekleyebilirsiniz.
                          </Alert>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => {
                              onClose();
                              // Trigger profile dialog with logos tab selected
                              const profileEvent = new CustomEvent('openProfileDialog', {
                                detail: { tabIndex: 1 } // 1 is the index for the Logos tab
                              });
                              window.dispatchEvent(profileEvent);
                            }}
                            sx={{ 
                              mb: 2,
                              fontSize: '0.8rem',
                              textTransform: 'none'
                            }}
                          >
                            Logo Eklemek İçin Tıklayın
                          </Button>
                        </Box>
                      ) : (
                        <>
                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel id="logo-select-label">Logo Seçin</InputLabel>
                            <Select
                              labelId="logo-select-label"
                              value={selectedLogoUrl}
                              label="Logo Seçin"
                              onChange={(e) => setSelectedLogoUrl(e.target.value)}
                            >
                              {userLogos.map((logo) => (
                                <MenuItem key={logo.id} value={logo.logo_url}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <img 
                                      src={logo.logo_url} 
                                      alt={logo.logo_name || 'logo'} 
                                      style={{ 
                                        width: 20, 
                                        height: 20, 
                                        marginRight: 8, 
                                        objectFit: 'contain' 
                                      }} 
                                    />
                                    {logo.logo_name || `Logo ${logo.id.substring(0, 6)}`}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel id="position-select-label">Konum</InputLabel>
                            <Select
                              labelId="position-select-label"
                              value={watermarkPosition}
                              label="Konum"
                              onChange={(e) => setWatermarkPosition(e.target.value)}
                            >
                              <MenuItem value="bottom-right">Sağ Alt</MenuItem>
                              <MenuItem value="bottom-left">Sol Alt</MenuItem>
                              <MenuItem value="top-right">Sağ Üst</MenuItem>
                              <MenuItem value="top-left">Sol Üst</MenuItem>
                              <MenuItem value="center">Orta</MenuItem>
                              <MenuItem value="tile">Döşeme</MenuItem>
                            </Select>
                          </FormControl>
                          
                          <Typography variant="body2" gutterBottom>
                            Opaklık: {Math.round(watermarkOpacity * 100)}%
                          </Typography>
                          <Slider
                            value={watermarkOpacity}
                            min={0.1}
                            max={1}
                            step={0.05}
                            onChange={(e, newValue) => setWatermarkOpacity(newValue)}
                            sx={{ mb: 2 }}
                          />
                          
                          <Typography variant="body2" gutterBottom>
                            Boyut: {Math.round(watermarkScale * 100)}%
                          </Typography>
                          <Slider
                            value={watermarkScale}
                            min={0.05}
                            max={0.5}
                            step={0.01}
                            onChange={(e, newValue) => setWatermarkScale(newValue)}
                          />
                          
                          <Button 
                            variant="text" 
                            size="small" 
                            onClick={() => {
                              onClose();
                              // Trigger profile dialog with logos tab selected
                              const profileEvent = new CustomEvent('openProfileDialog', {
                                detail: { tabIndex: 1 } // 1 is the index for the Logos tab
                              });
                              window.dispatchEvent(profileEvent);
                            }}
                            sx={{ 
                              mt: 2,
                              fontSize: '0.8rem',
                              textTransform: 'none',
                              color: 'primary.main'
                            }}
                          >
                            Logolarımı Yönet
                          </Button>
                        </>
                      )}
                    </Box>
                  ) : (
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={shareSettings.watermark}
                          onChange={(e) => handleSettingChange('watermark', e.target.checked)}
                        />
                      }
                      label="Standart Filigran Ekle"
                      sx={{ mb: 1, display: 'block', ml: 3 }}
                    />
                  )}
                </Box>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={shareSettings.allowDownload}
                      onChange={(e) => handleSettingChange('allowDownload', e.target.checked)}
                    />
                  }
                  label="İndirmeye İzin Ver"
                  sx={{ mb: 1, display: 'block' }}
                />
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={shareSettings.isPublic}
                      onChange={(e) => handleSettingChange('isPublic', e.target.checked)}
                    />
                  }
                  label="Herkese Açık Paylaşım"
                  sx={{ mb: 1, display: 'block' }}
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {shareSettings.isPublic 
                    ? 'Herkese açık paylaşımlar şifre olmadan erişilebilir ve arama motorlarında görünebilir.'
                    : 'Özel paylaşımlar sadece bağlantıya sahip kişiler tarafından görüntülenebilir.'}
                </Typography>
                
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 3 }}
                  onClick={createShareLink}
                  disabled={loading || (shareSettings.passwordEnabled && !shareSettings.password)}
                  startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
                >
                  {loading ? 'İşleniyor...' : 'Paylaşım Bağlantısı Oluştur'}
                </Button>
              </Box>
            </Box>
            
            {shareLink && (
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                borderRadius: '8px'
              }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Paylaşım Bağlantısı
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TextField
                    fullWidth
                    value={shareLink}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={linkCopied ? "Kopyalandı!" : "Kopyala"}>
                            <IconButton onClick={() => copyToClipboard(shareLink)}>
                              <ContentCopyIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Sosyal Medyada Paylaş
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <SocialButton onClick={() => shareToSocial('facebook')}>
                    <FacebookIcon />
                  </SocialButton>
                  <SocialButton onClick={() => shareToSocial('twitter')}>
                    <TwitterIcon />
                  </SocialButton>
                  <SocialButton onClick={() => shareToSocial('linkedin')}>
                    <LinkedInIcon />
                  </SocialButton>
                  <SocialButton onClick={() => shareToSocial('whatsapp')}>
                    <WhatsAppIcon />
                  </SocialButton>
                  <SocialButton onClick={() => shareToSocial('email')}>
                    <EmailIcon />
                  </SocialButton>
                  <Tooltip title="QR Kod (Yakında)">
                    <SocialButton disabled>
                      <QrCodeIcon />
                    </SocialButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : existingShares.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Henüz paylaşım bağlantısı oluşturulmamış.
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                  onClick={() => setTabValue(0)}
                >
                  Yeni Paylaşım Oluştur
                </Button>
              </Box>
            ) : (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                
                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                  </Alert>
                )}
                
                {existingShares.map((share) => (
                  <Box 
                    key={share.id}
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      borderRadius: '8px',
                      border: theme => `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPermissionIcon(share.permission)}
                        <Typography variant="subtitle2">
                          {share.permission === 'view' ? 'Sadece görüntüleme' : 
                           share.permission === 'comment' ? 'Görüntüleme ve yorum' : 
                           'Tam erişim'}
                        </Typography>
                        {share.password && (
                          <Tooltip title="Şifre korumalı">
                            <LockIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Box>
                      <Box>
                        <Chip 
                          size="small"
                          icon={<AccessTimeIcon fontSize="small" />}
                          label={getExpirationText(share.expires_at)}
                          color={!share.expires_at ? "success" : "default"}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={`${window.location.origin}/share/${share.id}`}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Kopyala">
                                <IconButton onClick={() => copyToClipboard(`${window.location.origin}/share/${share.id}`)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        Oluşturulma: {new Date(share.created_at).toLocaleDateString()}
                        {share.view_count > 0 && ` • ${share.view_count} görüntülenme`}
                      </Typography>
                      
                      <Button 
                        size="small" 
                        color="error" 
                        onClick={() => deleteShareLink(share.id)}
                        disabled={loading}
                      >
                        Sil
                      </Button>
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
